import { useState, useCallback, useEffect } from 'react';
import {
  ErpAuthProvider,
  fetchErpAuthStatus,
  getErpProviderLabel,
  redirectToErpAuthorization,
  redirectToQuickBooksAuthorization,
} from '@api/quickbooksAuth';
import { useNotificationStore } from '@store/notificationStore';

interface QuickBooksAuthState {
  isChecking: boolean;
  isAuthenticated: boolean;
  realmId?: string;
  authorizationUrl?: string;
}

interface UseQuickBooksAuthProps {
  enabled?: boolean; // Only run if explicitly enabled and user is authenticated
  provider?: ErpAuthProvider;
}

export { redirectToQuickBooksAuthorization };
export { redirectToErpAuthorization };

/**
 * Hook for QuickBooks OAuth authentication
 *
 * Background authentication check flow:
 * 1. On app start, checks backend auth status
 * 2. Backend validates if AuthClient has valid tokens
 * 3. If not authenticated, backend returns authorization URL
 * 4. Frontend redirects to that URL
 * 5. User logs in and approves in QB
 * 6. QB redirects back with code to backend callback endpoint
 * 7. Backend exchanges code for tokens
 */
export const useErpAuth = ({
  enabled = false,
  provider = 'quickbooks',
}: UseQuickBooksAuthProps = {}) => {
  const [authState, setAuthState] = useState<QuickBooksAuthState>({
    isChecking: enabled, // Only checking if enabled
    isAuthenticated: false,
  });
  const { addNotification } = useNotificationStore();
  const providerLabel = getErpProviderLabel(provider);

  /**
   * Check QB auth status on backend and redirect if needed
   */
  const checkAndInitializeAuth = useCallback(async () => {
    if (!enabled) return; // Don't run if not enabled

    try {
      console.log(`[ERP Auth] Starting ${providerLabel} auth status check...`);
      const response = await fetchErpAuthStatus(provider);

      console.log('[ERP Auth] Received auth status response:', response);

      const {
        is_authenticated,
        realm_id,
        authorization_url,
      } = response;

      if (is_authenticated && realm_id) {
        // Already authenticated - store state
        console.log(`[ERP Auth] ${providerLabel} is authenticated. Realm:`, realm_id);
        setAuthState({
          isChecking: false,
          isAuthenticated: true,
          realmId: realm_id,
        });
      } else if (authorization_url) {
        // Not authenticated - store URL for consumer to use
        console.log(`[ERP Auth] ${providerLabel} authorization URL available.`);
        setAuthState({
          isChecking: false,
          isAuthenticated: false,
          authorizationUrl: authorization_url,
        });
      } else {
        // No URL available, set error state
        console.warn(`[ERP Auth] No authorization URL provided for ${providerLabel}`);
        setAuthState({
          isChecking: false,
          isAuthenticated: false,
        });

        addNotification({
          type: 'error',
          message: `${providerLabel} authentication not available. Please contact support.`,
        });
      }
    } catch (error: any) {
      console.error(`[ERP Auth] Error during ${providerLabel} auth check:`, error);
      setAuthState({
        isChecking: false,
        isAuthenticated: false,
      });

      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        `Failed to check ${providerLabel} authentication status`;

      // Only show error if it's not a network issue
      if (error.message !== 'Network Error') {
        addNotification({
          type: 'error',
          message: errorMessage,
        });
      }
    }
  }, [enabled, addNotification, provider, providerLabel]);

  /**
   * Update isChecking when enabled prop changes
   */
  useEffect(() => {
    if (enabled) {
      // When enabled becomes true, mark as checking
      setAuthState((prev) => ({
        ...prev,
        isChecking: true,
      }));
    }
  }, [enabled]);

  /**
   * Check auth status when enabled
   * Only runs when enabled is true
   */
  useEffect(() => {
    if (!enabled) {
      console.log(`[ERP Auth] ${providerLabel} auth check disabled`);
      return;
    }

    console.log(`[ERP Auth] initializing ${providerLabel} auth check`);
    const initAuth = async () => {
      await checkAndInitializeAuth();
    };
    initAuth();
  }, [enabled, checkAndInitializeAuth, providerLabel]);

  return {
    authState,
    checkAndInitializeAuth,
  };
};

export const useQuickBooksAuth = ({ enabled = false }: UseQuickBooksAuthProps = {}) =>
  useErpAuth({ enabled, provider: 'quickbooks' });
